import * as Tabs from "@radix-ui/react-tabs";
import { useLocation, useNavigate } from "react-router";
import ViewLayout from "../../components/ViewLayout";
import HomeInfoContent from "./components/HomeInfoContent";
import PendingReportsContent from "./components/PendingReportsContent";
import AuthorshipsDocumentTab from "../documents/components/AuthorshipsDocumentTab";
import CourseCertificatesDocumentTab from "../documents/components/CourseCertificatesDocumentTab";
import InsurancePoliciesDocumentTab from "../documents/components/InsurancePoliciesDocumentTab";
import ReductionsDocumentTab from "../documents/components/ReductionsDocumentTab";
import LegalAidDocumentTab from "../documents/components/LegalAidDocumentTab";
import DispensationsDocumentTab from "../documents/components/DispensationsDocumentTab";
import TeachingsDocumentTab from "../documents/components/TeachingsDocumentTab";

type HomeDocumentTabValue =
  | "authorships"
  | "course-certificates"
  | "dispensations"
  | "policies"
  | "reductions"
  | "legal-aid"
  | "teachings";

const HOME_DOCUMENT_TABS: {
  value: HomeDocumentTabValue;
  trigger: string;
  title: string;
  description: string;
}[] = [
  {
    value: "course-certificates",
    trigger: "Mine kursusbeviser",
    title: "Mine kursusbeviser",
    description:
      "Her kan du se dine kursusbeviser, som endnu ikke er indberettet.",
  },
  {
    value: "policies",
    trigger: "Min forsikring og garanti",
    title: "Min forsikring",
    description: "Her kan du se dine forsikringspolicer.",
  },
  {
    value: "reductions",
    trigger: "Mine reduktioner",
    title: "Mine reduktioner",
    description:
      "Her kan du se dine reduktioner, som endnu ikke er indberettet.",
  },
  {
    value: "legal-aid",
    trigger: "Advokatvagt/retshjælp",
    title: "Advokatvagt/retshjælp",
    description:
      "Her kan du se din dokumentation for advokatvagt og retshjælp, som endnu ikke er indberettet.",
  },
  {
    value: "dispensations",
    trigger: "Dispensation",
    title: "Dispensation",
    description:
      "Her kan du se dine dispensationer, som endnu ikke er indberettet.",
  },
  {
    value: "teachings",
    trigger: "Undervisning",
    title: "Undervisning",
    description:
      "Her kan du se din undervisning, som endnu ikke er indberettet. Undervisning skal godkendes af sekretariatet, før den kan indberettes.",
  },
  {
    value: "authorships",
    trigger: "Forfatterskab",
    title: "Forfatterskab",
    description:
      "Her kan du se dine forfatterskaber, som endnu ikke er indberettet.",
  },
];

const DEFAULT_DOCUMENT_TAB: HomeDocumentTabValue = "course-certificates";

const isHomeDocumentTabValue = (value: string): value is HomeDocumentTabValue =>
  HOME_DOCUMENT_TABS.some((tab) => tab.value === value);

const getDocumentTabValueFromHash = (hash: string): HomeDocumentTabValue => {
  const hashValue = decodeURIComponent(hash.replace(/^#/, ""));

  return isHomeDocumentTabValue(hashValue) ? hashValue : DEFAULT_DOCUMENT_TAB;
};

const renderDocumentTabContent = ({
  description,
  title,
  value,
}: (typeof HOME_DOCUMENT_TABS)[number]) => {
  const tabProps = {
    description,
    detailPathPrefix: "dokumenter/",
    reported: false,
    showStatusColumn: false,
    title,
  };

  if (value === "course-certificates") {
    return <CourseCertificatesDocumentTab {...tabProps} showStatusColumn />;
  }

  if (value === "policies") {
    return <InsurancePoliciesDocumentTab {...tabProps} showStatusColumn />;
  }

  if (value === "reductions") {
    return <ReductionsDocumentTab {...tabProps} />;
  }

  if (value === "legal-aid") {
    return <LegalAidDocumentTab {...tabProps} />;
  }

  if (value === "dispensations") {
    return <DispensationsDocumentTab {...tabProps} />;
  }

  if (value === "teachings") {
    // Undervisning afventer sekretariatets godkendelse, så status siger mere end
    // "Ikke indberettet", som er alt, de andre manuelle typer kan vise her.
    return <TeachingsDocumentTab {...tabProps} showStatusColumn />;
  }

  return <AuthorshipsDocumentTab {...tabProps} />;
};

const HomeView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeDocumentTab = getDocumentTabValueFromHash(location.hash);

  const handleDocumentTabChange = (value: string) => {
    if (!isHomeDocumentTabValue(value)) {
      return;
    }

    void navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: `#${value}`,
      },
      { preventScrollReset: true },
    );
  };

  return (
    <ViewLayout
      title="Efteruddannelse"
      description="Her får du overblik over din obligatoriske efteruddannelse, herunder afventende indberetninger og dokumentation, der endnu ikke er indberettet."
      infoTitle="Sådan bruger du Efteruddannelse"
      infoDescription="Overblik over faneblade, upload og automatisk kontrol"
      infoText={<HomeInfoContent />}
    >
      <PendingReportsContent />

      <section className="cle-compliance-doc-dashboard mt-4">
        <Tabs.Root
          value={activeDocumentTab}
          onValueChange={handleDocumentTabChange}
        >
          <Tabs.List
            className="cle-compliance-doc-dashboard__tablist"
            aria-label="Tilgængelige dokumenter efter type"
          >
            {HOME_DOCUMENT_TABS.map(({ value, trigger }) => (
              <Tabs.Trigger
                key={value}
                id={`cle-home-doc-tab-${value}`}
                className="cle-compliance-doc-dashboard__tab"
                value={value}
              >
                {trigger}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {HOME_DOCUMENT_TABS.map((tab) => (
            <Tabs.Content
              key={tab.value}
              value={tab.value}
              className="cle-compliance-doc-dashboard__content"
            >
              {activeDocumentTab === tab.value
                ? renderDocumentTabContent(tab)
                : null}
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </section>
    </ViewLayout>
  );
};

export default HomeView;
